import React, { useState, useCallback, useRef } from "react"
import Modal from "../components/ui/Modal"
import Button from "../components/ui/Button"
import { XCircleIcon } from "@heroicons/react/20/solid"
import classNames from "../utils/classNames"

interface UseConfirmOptions {
  title: string
  description?: string | JSX.Element
  icon?: any // TODO: component definition
  colorName?: string
  ctaColorName?: string
  ctaLabel?: string
  onClose?: () => void
}

interface UseConfirmReturn {
  showConfirm: (options: UseConfirmOptions) => Promise<boolean>
  ConfirmModal: any
}

export const useConfirm = (): UseConfirmReturn => {
  const [isOpen, setIsOpen] = useState(false)
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState<string | JSX.Element>()
  const [icon, setIcon] = useState<any>()
  const [colorName, setColorName] = useState("blue")
  const [ctaColorName, setCtaColorName] = useState("blue")
  const [ctaLabel, setCtaLabel] = useState("Confirm")
  const resolveRejectRef = useRef<(value: boolean) => void>()
  const onCloseRef = useRef<(() => void) | null>(null)

  const showConfirm = useCallback((options: UseConfirmOptions): Promise<boolean> => {
    setTitle(options.title)
    setDescription(options.description)
    setIcon(options.icon)
    setColorName(options.colorName || "blue")
    setCtaColorName(options.ctaColorName || "blue")
    setCtaLabel(options.ctaLabel || "Confirm")
    onCloseRef.current = options.onClose || null
    setIsOpen(true)
    return new Promise<boolean>((resolve) => {
      resolveRejectRef.current = resolve
    })
  }, [])

  const handleConfirm = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    if (resolveRejectRef.current) {
      resolveRejectRef.current(true)
    }
    setIsOpen(false)
    if (onCloseRef.current) {
      onCloseRef.current()
    }
  }, [])

  const handleCancel = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    if (resolveRejectRef.current) {
      resolveRejectRef.current(false)
    }
    setIsOpen(false)
    if (onCloseRef.current) {
      onCloseRef.current()
    }
  }, [])

  const ConfirmModal = () => {
    const IconClass = icon || XCircleIcon

    return (
      <Modal isDynamicWidth open={isOpen} onClose={handleCancel}>
        <div className="">
          <div className="mb-12 sm:mr-8">
            <div className="sm:flex sm:items-start place-items-center content-center">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-50 sm:mx-0 sm:h-10 sm:w-10">
                <IconClass className={classNames(`h-12 w-12 text-${colorName}-400`)} />
              </div>
              <div className="mt-3 self-center text-center sm:mt-0 sm:ml-4 sm:text-left">
                <div className="text-lg leading-6 font-medium text-gray-900 mr-2">{title}</div>
                {description && <div className="mt-2">{description}</div>}
              </div>
            </div>
          </div>
          <div className="flex justify-end items-center">
            <Button
              type="button"
              variant="gray"
              className="sm:ml-2 mr-2.5 w-1/2 sm:w-32 rounded-md"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button variant={ctaColorName} onClick={handleConfirm} className="w-1/2 sm:w-32">
              {ctaLabel}
            </Button>
          </div>
        </div>
      </Modal>
    )
  }

  return { showConfirm, ConfirmModal }
}
